import React, {Component} from "react";
import {connect} from 'react-redux';
import {Button, FormControl, Grid, IconButton, MenuItem, Select, Stack, TextField, Tooltip} from "@mui/material";
import {updateSlideData, updateSlideState} from "../../../action/maps.state.action";
import {ReplayOutlined, SaveOutlined} from "@mui/icons-material";
import cookie from "react-cookies";
import {getSettingComponent} from "../utils/settingsUtils";
import {degreeToRadian} from "../../../utils/utils";
import {RotationKey, SettingsKeys} from "../keys/Settings";
import {LoadingText} from "../components/components";
import {
	createViewerSetting,
	loadViewerSettings,
	saveViewerSetting,
	updateViewerSettingLocally,
} from "../../../action/viewer.settings.actions";

const isSuperUser = () => cookie.loadAll().superuser === "true";


class SettingsApp extends Component {

	constructor(props) {
		super(props);
		this.rowHeight = '89px';

		this.state = {};
		this.props.dispatch(loadViewerSettings());
	}

	backup = () => {
		// Backup the *shared* preset (without local override) so that Restore
		// returns the user to the admin-stored values.
		if (!this.props.slideState.backupViewerSettings ||
			this.props.slideState.backupViewerSettings.id !== this.props.sharedViewerSetting.id)
			this.props.slideState.backupViewerSettings = {...this.props.sharedViewerSetting}
	}


	// Non-admins are restricted to frontend-only changes: update the slide
	// state in redux without sending a patch to the backend. Admins still
	// persist changes via updateSlideData.
	updateSlideField = data => {
		if (isSuperUser()) {
			this.props.dispatch(updateSlideData(this.props.activeMapId, this.props.slide.id, data));
		} else {
			this.props.dispatch(updateSlideState(this.props.activeMapId, {
				slide_data: {...this.props.slide, ...data},
			}));
		}
	}

	clearLocalOverride = () =>
		this.props.dispatch(updateSlideState(this.props.activeMapId, {
			localViewerSettingOverride: null,
		}));

	updateViewerSettingId = id => {
		// Switching presets discards any local override since it was tied to
		// the previous preset.
		this.clearLocalOverride();
		this.updateSlideField({settings: id});
	}

	restoreSettings = () => {
		if (this.props.slideState.backupViewerSettings) {
			// Drop the local override so the slide reverts to the stored preset.
			this.clearLocalOverride();
			if (isSuperUser()) {
				// Only admins can repair a preset they may have mutated.
				this.props.dispatch(updateViewerSettingLocally(
					this.props.slideState.backupViewerSettings.id,
					this.props.slideState.backupViewerSettings));
			}
			this.updateViewerSettingId(this.props.slideState.backupViewerSettings.id);
		}
	}

    updateRotation = value => {
		this.props.slideState.slidemap.getView().setRotation(degreeToRadian(value));
		this.updateSlideField({viewer_rotation: parseInt(value)});
	}

	update = (setting, value) => {
		const key = setting.name.toLowerCase();
		const intValue = parseInt(value);
		if (isSuperUser()) {
			// Admins edit the shared preset directly; Save then persists it.
			this.props.dispatch(updateViewerSettingLocally(this.props.sharedViewerSetting.id, {
				[key]: intValue,
			}));
		} else {
			// Non-admin edits live as a per-slide override that never touches
			// the shared preset and is wiped on page reload.
			const override = this.props.slideState.localViewerSettingOverride || {};
			this.props.dispatch(updateSlideState(this.props.activeMapId, {
				localViewerSettingOverride: {...override, [key]: intValue},
			}));
		}
	}

	saveSettings = () => {
		const {id, ...rest} = this.props.sharedViewerSetting;
		this.props.dispatch(saveViewerSetting(id, rest));
		// Also persist the slide-level fields (chosen preset + rotation) for admins.
		this.props.dispatch(updateSlideData(this.props.activeMapId, this.props.slide.id, {
			settings: this.props.slide.settings,
			viewer_rotation: this.props.slide.viewer_rotation,
		}));
	}

	getViewerSettingsDropdown = () =>
		<Tooltip title={"Choose Settings"} placement={"left"}>
			<Select value={this.props.sharedViewerSetting} MenuProps={{
				anchorOrigin: {
					vertical: "bottom",
					horizontal: "right",
				},
				transformOrigin: {
					vertical: "top",
					horizontal: "right",
				}
			}} onChange={e => this.updateViewerSettingId(e.target.value.id)}>
				{Object.values(this.props.viewerSettings)
					.filter(viewerSetting => viewerSetting !== this.props.bloodViewerSetting)
					.map(viewerSetting => <MenuItem value={viewerSetting}>{viewerSetting.name}</MenuItem>)}
				{isSuperUser() &&
					<TextField color='secondary' margin='normal' variant={"outlined"} placeholder={"+ new"}
							   size="small" sx={{px: 1, mx: 0, width: 80,}}
							   inputProps={{maxLength: 15}}
							   onKeyDown={(e) =>
								   e.keyCode === 13 ? [this.props.dispatch(createViewerSetting({
									   ...this.props.sharedViewerSetting,
									   name: e.target.value,
								   })), e.target.value = ''] : e.stopPropagation()}/>}
			</Select>
		</Tooltip>

	render() {
		if (!this.props.viewerSetting)
			return <LoadingText/>;

		this.backup();

		let viewerSettingsSelect =
			<Grid container paddingBottom={1} direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
				<Tooltip title={"Restore"} placement={"right"}>
					<IconButton color={"secondary"} onClick={() => this.restoreSettings()}>
						<ReplayOutlined/>
					</IconButton>
				</Tooltip>
				<FormControl variant="standard">
					{this.getViewerSettingsDropdown()}
				</FormControl>
			</Grid>

		return <Stack direction={"column"} spacing={2}>
			{viewerSettingsSelect}
			{getSettingComponent(RotationKey, this.props.slide.viewer_rotation, this.updateRotation)}
			{SettingsKeys.map(setting => getSettingComponent(setting,
				this.props.viewerSetting[setting.name.toLowerCase()], value => this.update(setting, value)))}
			{isSuperUser() &&
				<Tooltip title={"Save settings to backend"} placement={"left"}>
					<Button variant={"contained"} color={"secondary"} startIcon={<SaveOutlined/>}
							onClick={() => this.saveSettings()}>
						Save
					</Button>
				</Tooltip>}
		</Stack>
	}
}

export const mapStateToProps = (state) => {
	let {activeMapId} = state.gammaStateReducer;
	let {slideState} = state.mapsStateReducer[activeMapId];
	let slide = slideState.slide_data;
	let viewerSettings = state.viewerSettingsReducer;
	let sharedViewerSetting = viewerSettings[slide.settings];
	// Per-slide local override is layered on top of the shared preset so that
	// non-admin users see their changes without polluting the shared preset
	// (or affecting any other slide using the same preset). The override only
	// lives in redux and is cleared on page reload.
	let localOverride = slideState.localViewerSettingOverride || {};
	let viewerSetting = sharedViewerSetting ? {...sharedViewerSetting, ...localOverride} : sharedViewerSetting;
	let bloodViewerSetting = viewerSettings[slide.bloodsettings];

	return {
		activeMapId, slideState, slide, viewerSettings,
		viewerSetting, sharedViewerSetting, bloodViewerSetting,
	};
}

export default connect(mapStateToProps)(SettingsApp);
